import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import ReactImageGallery from "react-image-gallery";
import {
  faBed,
  faBath,
  faRulerCombined,
  faClock,
  faPhone,
  faEnvelope,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import constants from "../constants/constants";
import placeholder from "../assets/images/house-placeholder.webp";
import SEO, { pageSEO } from "../components/seo.component";
import InquiryCard from "./inquiryCard.component";
import { useUserParams } from "../hooks/useUserParams";
// remove the below two lines from client websites. These are dummy data
import data from "../data.json";
import imgs from "./temp.json";

export default function Listing() {
  const { id } = useParams();
  const {
    getCurrentAgentName,
    getCurrentAgentNumber,
    getCurrentAgentEmail,
    getCurrentHeadshot,
  } = useUserParams();

  const prop = data.value.find(prop => prop.ListingKey === id); //dummy listings
  const [property, setProperty] = useState(prop); //dummy listings

  // const [property, setProperty] = useState({});
  // const [images, setImages] = useState([]);

  //To create build with dummy listings
  const [images, setImages] = useState(property.Images
    .map(img => ({
      original:
        img,

      thumbnail:
        img
    }))
  );

  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [windowSize, setWindowSize] = useState(window.innerWidth);

  const onWindowResize = () => {
    setWindowSize(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener("resize", onWindowResize);
    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
  }, []);

  async function fetchAPI(url) {
    const body = {
      url,
      needAuth: true,
      bearerToken: constants.TOKEN
    };

    const response =
      constants.ENVIRONMENT === "production"
        ?
        await fetch(
          `https://${constants.DOMAIN}/api/${url}`
        )
        :
        await fetch(
          `http://${constants.URL}/metaGet`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify(body)
          }
        );
    if (!response.ok)
      throw new Error("API Error");
    return response.json();
  }

  async function loadListing() {
    try {
      setLoading(true);

      const url = constants.ENVIRONMENT === "production" ?
        `odata/Property(Q)$filter=ListingKey(S)eq(S)'${id}'`
        :
        `https://query.ampre.ca/odata/Property?$filter=ListingKey eq '${id}'`;

      const propertyData =
        await fetchAPI(url);

      setProperty(
        propertyData.value?.[0] || {}
      );

      let mediaUrl;
      if (constants.ENVIRONMENT === "production") {
        mediaUrl =
          `odata/Media(Q)$filter=ResourceRecordKey(S)eq(S)'${id}'(S)and(S)(ImageSizeDescription(S)eq(S)'LargestNoWatermark'(S)or(S)ImageSizeDescription(S)eq(S)'Thumbnail')`;
      } else {
        mediaUrl =
          `https://query.ampre.ca/odata/Media?$filter=ResourceRecordKey eq '${id}' and (ImageSizeDescription eq 'LargestNoWatermark' or ImageSizeDescription eq 'Thumbnail')`;
      }

      const media =
        await fetchAPI(
          mediaUrl
        );

      console.log("media", media);

      const formatted =
        media.value?.reduce((acc, item) => {
          const existing =
            acc[item.MediaObjectID] || {};

          acc[item.MediaObjectID] = {
            ...existing,
            [item.ImageSizeDescription]: item
          };
          return acc;
        }, {});

      setImages(
        Object.values(formatted || {})
          .map(img => ({
            original:
              img?.LargestNoWatermark?.MediaURL || placeholder,

            thumbnail:
              img?.Thumbnail?.MediaURL || placeholder
          }))
      );
    }
    catch (err) {
      console.log(err);
    }
    finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadListing();
  }, []);

  const seoConfig =
    pageSEO.listing(property);

  return (
    <>
      <SEO {...seoConfig} />

      <section className="min-h-screen bg-harbour-background pt-40 pb-24">
        <div className="mx-auto max-w-[1500px] px-6">
          {/* Header */}
          <div className="mb-12 flex flex-col justify-between gap-8 lg:flex-row">
            <div>
              <p className="flex items-center gap-3 uppercase tracking-[0.45em] text-harbour-blue">
                <span className="h-px w-10 bg-harbour-accent" />
                Property Details
              </p>

              <h1 className="mt-6 max-w-3xl font-heading text-5xl leading-tight text-harbour-navy md:text-6xl">
                {property?.StreetNumber}
                {" "}
                {property?.StreetName}
                {" "}
                {property?.StreetSuffix}
              </h1>

              <p className="mt-5 flex items-center gap-3 text-lg text-harbour-navy">
                <FontAwesomeIcon
                  icon={faLocationDot}
                  className="text-harbour-accent"
                />

                {property?.City},
                {" "}
                {property?.StateOrProvince}
                {" "}
                {property?.PostalCode}
              </p>
            </div>

            <div className="rounded-[28px] border border-harbour-border bg-white px-10 py-8 shadow-sm">
              <p className="text-xs uppercase tracking-[0.35em] text-harbour-muted">
                Listed Price
              </p>

              <h2 className="mt-3 font-heading text-4xl text-harbour-navy">
                ${property?.ListPrice?.toLocaleString()}
              </h2>

              <p className="mt-4 text-sm text-harbour-muted">
                <FontAwesomeIcon
                  icon={faClock}
                  className="mr-2 text-harbour-accent"
                />
                Listed{" "}
                {
                  property?.OriginalEntryTimestamp &&
                  new Date(property.OriginalEntryTimestamp)
                    .toLocaleDateString()
                }
              </p>
            </div>
          </div>

          <div className="grid gap-10 lg:grid-cols-[1fr_360px]">
            <div className="space-y-10">
              {/* Gallery */}
              <div className="overflow-hidden rounded-[32px] bg-white p-5 shadow-[0_25px_70px_rgba(14,78,127,.08)]">
                {
                  loading
                    ?
                    <div className="h-[600px] animate-pulse rounded-2xl bg-harbour-sky" />
                    :
                    <ReactImageGallery
                      items={images}
                      loading="eager"
                      thumbnailPosition={
                        windowSize > 950
                          ?
                          "right"
                          :
                          "bottom"
                      }
                      showThumbnails={
                        windowSize > 950
                      }
                      showPlayButton={false}
                    />
                }
              </div>

              {/* Features */}
              <div className="rounded-[32px] border border-harbour-border bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-heading text-4xl text-harbour-navy">
                  Property Features
                </h2>

                <div className="mt-8 grid gap-5 md:grid-cols-3">
                  {
                    property?.PropertyType?.toLowerCase()
                      .includes("residential")
                    &&
                    <>
                      <Feature
                        icon={faBed}
                        title="Bedrooms"
                        text={
                          property?.BedroomsTotal ||
                          "--"
                        }
                      />

                      <Feature
                        icon={faBath}
                        title="Bathrooms"
                        text={
                          property?.BathroomsTotalInteger ||
                          "--"
                        }
                      />
                    </>
                  }

                  <Feature
                    icon={faRulerCombined}
                    title="Size"
                    text={
                      property?.LivingAreaRange ||
                      property?.BuildingAreaTotal ||
                      "--"
                    }
                  />
                </div>
              </div>

              {/* Description */}
              <div className="rounded-[32px] border border-harbour-border bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-heading text-4xl text-harbour-navy">
                  About This Property
                </h2>

                <p className="mt-6 leading-8 text-harbour-muted">
                  {
                    !detailsOpen &&
                      property?.PublicRemarks?.length > 700

                      ?

                      property.PublicRemarks.substring(0, 700) + "..."

                      :

                      property?.PublicRemarks
                  }
                </p>

                {
                  property?.PublicRemarks?.length > 700 &&
                  <button
                    onClick={() =>
                      setDetailsOpen(!detailsOpen)
                    }
                    className="mt-5 text-sm uppercase tracking-[0.25em] text-harbour-blue transition hover:text-harbour-navy"
                  >
                    {
                      detailsOpen
                        ?
                        "Show Less"
                        :
                        "Read More"
                    }
                  </button>
                }
              </div>

              {/* Additional Information */}
              <div className="rounded-[32px] border border-harbour-border bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-heading text-4xl text-harbour-navy">
                  Property Information
                </h2>
                <div className="mt-8 grid gap-x-10 gap-y-6 md:grid-cols-2">
                  <Info
                    label="Property Type"
                    value={property?.PropertyType}
                  />

                  <Info
                    label="Property Sub Type"
                    value={property?.PropertySubType}
                  />

                  <Info
                    label="Heating"
                    value={property?.HeatSource}
                  />

                  <Info
                    label="HVAC"
                    value={property?.HeatType}
                  />

                  <Info
                    label="Garage"
                    value={property?.GarageType}
                  />

                  <Info
                    label="Parking"
                    value={property?.ParkingTotal}
                  />

                  <Info
                    label="Exposure"
                    value={property?.DirectionFaces}
                  />

                  <Info
                    label="Lot Size"
                    value={
                      `${property?.LotSizeRangeAcres || "--"} ${property?.LotSizeUnits || ""}`
                    }
                  />

                  <Info
                    label="Basement"
                    value={
                      property?.Basement?.length
                        ?
                        property.Basement.join(", ")
                        :
                        "--"
                    }
                  />

                  <Info
                    label="Office"
                    value={
                      property?.ListOfficeName
                    }
                  />

                  <Info
                    label="Board"
                    value={
                      property?.ListAOR
                    }
                  />

                </div>
              </div>

              {
                property?.PublicRemarksExtras &&
                <p className="rounded-2xl bg-harbour-sky p-6 text-lg leading-8 text-harbour-text">
                  {property.PublicRemarksExtras}
                </p>
              }

              {/* Mortgage Calculator */}
              <div className="rounded-[32px] border border-harbour-border bg-white p-8 shadow-sm lg:p-10">
                <h2 className="font-heading text-4xl text-harbour-navy">
                  Affordability Calculator
                </h2>

                <div className="mt-8 overflow-hidden rounded-2xl bg-harbour-background">
                  <iframe
                    src={`https://www.mortgagecalculator.org/webmasters/?downpayment=80000&homevalue=${property?.ListPrice}&loanamount=320000&interestrate=7&loanterm=30&propertytax=2700&pmi=1&homeinsurance=1500&monthlyhoa=0`}
                    scrolling="no"
                    style={{
                      width: "100%",
                      height:
                        windowSize >= 768
                          ?
                          "1800px"
                          :
                          "2200px"
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="sticky top-28 h-fit space-y-6">
              <div className="rounded-[32px] border border-harbour-border bg-white p-8 text-center shadow-sm">
                <div className="relative mx-auto h-36 w-36">
                  <img
                    src={getCurrentHeadshot()}
                    alt={getCurrentAgentName()}
                    className="h-full w-full rounded-full object-cover border-4 border-harbour-accent"
                  />
                </div>

                <h3 className="mt-6 font-heading text-3xl text-harbour-navy">
                  {getCurrentAgentName()}
                </h3>

                <p className="mt-2 text-sm uppercase tracking-[0.3em] text-harbour-muted">
                  Real Estate Advisor
                </p>

                <div className="mt-8 space-y-4 text-sm text-harbour-text">
                  <p className="flex items-center justify-center gap-3">
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-harbour-accent"
                    />
                    {getCurrentAgentNumber()}
                  </p>

                  <p className="flex items-center justify-center gap-3">
                    <FontAwesomeIcon
                      icon={faEnvelope}
                      className="text-harbour-accent"
                    />
                    {getCurrentAgentEmail()}
                  </p>
                </div>
              </div>

              <InquiryCard />
            </aside>
          </div>
        </div>
      </section>
    </>
  );
}





function Feature({
  icon,
  title,
  text
}) {

  return (

    <div className="rounded-2xl bg-harbour-background p-5 transition hover:bg-harbour-sky">


      <FontAwesomeIcon

        icon={icon}

        className="text-2xl text-harbour-accent"

      />


      <p className="mt-4 text-xs uppercase tracking-[0.25em] text-harbour-muted">

        {title}

      </p>


      <p className="mt-2 font-heading text-3xl text-harbour-navy">

        {text}

      </p>


    </div>

  );

}





function Info({
  label,
  value
}) {


  return (

    <div className="flex justify-between gap-4 border-b border-harbour-border pb-3">


      <span className="text-sm uppercase tracking-[0.18em] text-harbour-muted">

        {label}

      </span>



      <span className="text-right text-sm font-medium text-harbour-text">

        {value || "--"}

      </span>


    </div>

  );

}
